/**
 * Booking form.
 *
 * The applicable policy is shown before the person commits, a repeat is
 * previewed occurrence by occurrence, and a refusal is rendered where it
 * happened rather than as a toast.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { Dialog } from "@/components/Dialog";
import { Icon } from "@/components/Icon";
import { Notice } from "@/components/States";
import { StatusPill } from "@/components/StatusPill";
import { ApiError, api } from "@/lib/api";
import {
  addMinutes,
  formatDate,
  formatDuration,
  formatTime,
  timeZoneOf,
  toLocalInput,
} from "@/lib/format";
import { useSession } from "@/lib/session";
import { useToast } from "@/lib/toast";
import type { Booking, Policy, SearchRoom, UserBrief } from "@/lib/types";

interface Occurrence {
  index: number;
  start_at: string;
  end_at: string;
  ok: boolean;
  reason: string | null;
  requires_approval: boolean;
}

interface SeriesPlan {
  bookable: number;
  blocked: number;
  requires_approval: boolean;
  occurrences: Occurrence[];
}

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const localZone = timeZoneOf(null);

export function BookingForm({
  room,
  start,
  durationMinutes,
  onClose,
  onBooked,
}: {
  room: SearchRoom;
  start: Date;
  durationMinutes: number;
  onClose: () => void;
  onBooked: () => void;
}) {
  const { can } = useSession();
  const { notify } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [meetingLink, setMeetingLink] = useState("");
  const [attendees, setAttendees] = useState("");
  const [startAt, setStartAt] = useState(toLocalInput(start));
  const [duration, setDuration] = useState(durationMinutes);
  const [repeats, setRepeats] = useState(false);
  const [frequency, setFrequency] = useState<"daily" | "weekly" | "monthly">("weekly");
  const [weekdays, setWeekdays] = useState<number[]>([start.getDay() === 0 ? 6 : start.getDay() - 1]);
  const [count, setCount] = useState(8);
  const [bookedFor, setBookedFor] = useState<UserBrief | null>(null);
  const [peopleQuery, setPeopleQuery] = useState("");
  const [plan, setPlan] = useState<SeriesPlan | null>(null);
  const [skipConflicts, setSkipConflicts] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [confirmed, setConfirmed] = useState<Booking | null>(null);
  const [seriesResult, setSeriesResult] = useState<SeriesPlan | null>(null);

  const startDate = new Date(startAt);
  const endDate = addMinutes(startDate, duration);

  const { data: detail } = useQuery({
    queryKey: ["room-policy", room.id],
    queryFn: () => api.get<{ policy: Policy | null }>(`/portal/rooms/${room.id}`),
  });
  const policy = detail?.policy ?? null;

  const { data: people } = useQuery({
    queryKey: ["people", peopleQuery],
    queryFn: () => api.get<{ people: UserBrief[] }>("/portal/people", { q: peopleQuery }),
    enabled: can("booking.on_behalf") && peopleQuery.length >= 2,
  });

  function payload() {
    return {
      room_id: room.id,
      subject: subject.trim() || "Meeting",
      start_at: startDate.toISOString(),
      end_at: endDate.toISOString(),
      description: description.trim() || null,
      meeting_link: meetingLink.trim() || null,
      attendees: attendees
        .split(/[,;\s]+/)
        .map((entry) => entry.trim())
        .filter(Boolean),
      booked_for_id: bookedFor?.id ?? null,
      recurrence: repeats
        ? {
            frequency,
            interval: 1,
            by_weekdays: frequency === "weekly" ? weekdays : [],
            count,
          }
        : null,
      skip_conflicting_occurrences: skipConflicts,
    };
  }

  const previewMutation = useMutation({
    mutationFn: () => api.post<SeriesPlan>("/portal/bookings/preview", payload()),
    onSuccess: (result) => {
      setPlan(result);
      setError(null);
    },
    onError: (caught) => setError(caught instanceof ApiError ? caught : null),
  });

  const createMutation = useMutation({
    mutationFn: () =>
      api.post<Booking & { plan?: SeriesPlan; series?: unknown }>("/portal/bookings", payload()),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["portal-rooms"] });
      queryClient.invalidateQueries({ queryKey: ["portal-bookings"] });
      if ("plan" in result && result.plan) {
        setSeriesResult(result.plan as SeriesPlan);
      } else {
        setConfirmed(result);
      }
    },
    onError: (caught) => {
      if (caught instanceof ApiError) {
        setError(caught);
        if (caught.details?.occurrences) setPlan(caught.details as unknown as SeriesPlan);
      }
    },
  });

  // ------------------------------------------------------- confirmation view
  if (confirmed) {
    const pending = confirmed.status === "pending_approval";
    return (
      <Dialog
        open
        title={pending ? "Request sent" : "Room booked"}
        onClose={() => {
          onBooked();
          notify(pending ? "Your request was sent for approval." : `${room.name} is booked.`);
        }}
        footer={
          <>
            <button className="btn btn--secondary" onClick={() => { onBooked(); }}>
              Book another
            </button>
            <button
              className="btn btn--primary"
              onClick={() => navigate(`/bookings/${confirmed.id}`)}
            >
              View booking
            </button>
          </>
        }
      >
        <div className="stack" style={{ gap: "var(--space-5)" }}>
          <div className="row" style={{ gap: "var(--space-3)" }}>
            <StatusPill tone={pending ? "soon" : "available"}>
              {pending ? "Waiting for approval" : "Confirmed"}
            </StatusPill>
          </div>
          <div className="detail-list">
            <div className="detail-list__row">
              <span className="detail-list__key">Room</span>
              <span className="detail-list__value">
                {room.name} · {room.location_name} · {room.capacity} seats
              </span>
            </div>
            <div className="detail-list__row">
              <span className="detail-list__key">When</span>
              <span className="detail-list__value">
                {formatDate(confirmed.start_at, room.timezone)},{" "}
                {formatTime(confirmed.start_at, room.timezone)}–
                {formatTime(confirmed.end_at, room.timezone)}
              </span>
            </div>
            <div className="detail-list__row">
              <span className="detail-list__key">Subject</span>
              <span className="detail-list__value">{confirmed.subject}</span>
            </div>
          </div>
          {pending ? (
            <Notice tone="info" title="This room needs approval">
              {room.approval_reason || "An administrator will review your request."} You will
              be notified as soon as it is decided. The room is not held until then.
            </Notice>
          ) : policy?.check_in_required ? (
            <Notice tone="info" title="Remember to check in">
              Check in from the room tablet or this portal within{" "}
              {policy.check_in_window_after_minutes} minutes of the start, or the room is
              released for someone else.
            </Notice>
          ) : null}
        </div>
      </Dialog>
    );
  }

  // ------------------------------------------------------- series result view
  if (seriesResult) {
    return (
      <Dialog
        open
        title="Repeating booking created"
        onClose={() => { onBooked(); notify(`${seriesResult.bookable} meetings booked.`); }}
        footer={
          <button className="btn btn--primary" onClick={() => { onBooked(); }}>
            Done
          </button>
        }
      >
        <div className="stack" style={{ gap: "var(--space-4)" }}>
          <Notice tone="success" title={`${seriesResult.bookable} occurrences booked`}>
            {seriesResult.blocked > 0
              ? `${seriesResult.blocked} date(s) were skipped because the room was not free.`
              : "Every date in the series was available."}
          </Notice>
          <OccurrenceList plan={seriesResult} timezone={room.timezone} />
        </div>
      </Dialog>
    );
  }

  // -------------------------------------------------------------- form view
  const conflictDetails = error?.details as { conflicts?: { start_at: string; end_at: string }[] } | undefined;

  return (
    <Dialog
      open
      width="wide"
      title={`Book ${room.name}`}
      description={`${room.capacity} seats · ${room.location_name ?? ""}${room.floor ? ` · ${room.floor}` : ""}`}
      onClose={onClose}
      dismissable={!subject && !description}
      footer={
        <>
          <button className="btn btn--secondary" onClick={onClose}>Cancel</button>
          {repeats && !plan && (
            <button
              className="btn btn--secondary"
              onClick={() => previewMutation.mutate()}
              disabled={previewMutation.isPending}
            >
              {previewMutation.isPending ? "Checking…" : "Check dates"}
            </button>
          )}
          <button
            className="btn btn--primary"
            onClick={() => createMutation.mutate()}
            disabled={createMutation.isPending}
          >
            {createMutation.isPending
              ? "Booking…"
              : room.requires_approval
                ? "Send request"
                : repeats
                  ? `Book ${plan?.bookable ?? count} meetings`
                  : "Confirm booking"}
          </button>
        </>
      }
    >
      <div className="stack" style={{ gap: "var(--space-5)" }}>
        {error && (
          <Notice
            tone={error.isConflict ? "warning" : "error"}
            title={error.isConflict ? "This time is not available" : "The booking could not be saved"}
          >
            {error.message}
            {conflictDetails?.conflicts?.length ? (
              <ul style={{ marginTop: "var(--space-2)" }}>
                {conflictDetails.conflicts.map((conflict) => (
                  <li key={conflict.start_at} className="t-caption">
                    Booked {formatTime(conflict.start_at, room.timezone)}–
                    {formatTime(conflict.end_at, room.timezone)}
                  </li>
                ))}
              </ul>
            ) : null}
          </Notice>
        )}

        {room.requires_approval && !error && (
          <Notice tone="info" title="This room needs approval">
            {room.approval_reason} Your request will be sent to an administrator; the room
            is not held until it is approved.
          </Notice>
        )}

        <div className="form-grid">
          <div className="field form-grid--full">
            <label className="field__label" htmlFor="subject">Meeting title</label>
            <input id="subject" className="input" autoFocus value={subject}
              onChange={(event) => setSubject(event.target.value)}
              placeholder="Design review" />
            <p className="field__hint">
              {policy?.show_meeting_title
                ? "Shown on the door tablet."
                : "This room shows only “Occupied” on the door."}
            </p>
          </div>

          <div className="field">
            <label className="field__label" htmlFor="start">Starts</label>
            <input id="start" className="input" type="datetime-local" step={900}
              value={startAt} onChange={(event) => setStartAt(event.target.value)} />
          </div>

          <div className="field">
            <label className="field__label" htmlFor="dur">Length</label>
            <select id="dur" className="select" value={duration}
              onChange={(event) => setDuration(Number(event.target.value))}>
              {[15, 30, 45, 60, 90, 120, 180, 240].map((minutes) => (
                <option key={minutes} value={minutes}
                  disabled={
                    Boolean(policy) &&
                    (minutes < policy!.min_duration_minutes ||
                      minutes > policy!.max_duration_minutes)
                  }>
                  {formatDuration(minutes)}
                </option>
              ))}
            </select>
            <p className="field__hint">
              {/* The picker works in the viewer's own zone; when the room is
                  elsewhere, both times are named so nobody misreads the span. */}
              Ends at {formatTime(endDate.toISOString(), localZone)}
              {room.timezone && room.timezone !== localZone && (
                <>
                  {" · "}
                  {formatTime(startDate.toISOString(), room.timezone)}–
                  {formatTime(endDate.toISOString(), room.timezone)} in {room.timezone}
                </>
              )}
            </p>
          </div>

          {can("booking.on_behalf") && (
            <div className="field form-grid--full">
              <label className="field__label" htmlFor="behalf">Booking for</label>
              {bookedFor ? (
                <div className="row" style={{ gap: "var(--space-2)" }}>
                  <span className="pill pill--accent">
                    <span className="pill__dot" aria-hidden="true" />
                    {bookedFor.display_name}
                  </span>
                  <button type="button" className="btn btn--ghost btn--compact"
                    onClick={() => { setBookedFor(null); setPeopleQuery(""); }}>
                    Change
                  </button>
                </div>
              ) : (
                <>
                  <input id="behalf" className="input" value={peopleQuery}
                    onChange={(event) => setPeopleQuery(event.target.value)}
                    placeholder="Search a colleague, or leave empty to book for yourself" />
                  {Boolean(people?.people.length) && (
                    <div className="chips" style={{ marginTop: "var(--space-2)" }}>
                      {people!.people.map((person) => (
                        <button key={person.id} type="button" className="chip"
                          onClick={() => setBookedFor(person)}>
                          {person.display_name}
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
              <p className="field__hint">
                The booking is recorded as theirs; you are recorded as the person who made it.
              </p>
            </div>
          )}

          <div className="field form-grid--full">
            <label className="field__label" htmlFor="attendees">Attendees (optional)</label>
            <input id="attendees" className="input" value={attendees}
              onChange={(event) => setAttendees(event.target.value)}
              placeholder="name@company.com, name2@company.com" />
          </div>

          <div className="field form-grid--full">
            <label className="field__label" htmlFor="link">Meeting link (optional)</label>
            <input id="link" className="input" value={meetingLink}
              onChange={(event) => setMeetingLink(event.target.value)}
              placeholder="https://…" />
          </div>

          <div className="field form-grid--full">
            <label className="field__label" htmlFor="notes">Notes (optional)</label>
            <textarea id="notes" className="textarea" value={description}
              onChange={(event) => setDescription(event.target.value)} />
          </div>
        </div>

        {policy?.allow_recurring && (
          <div className="stack" style={{ gap: "var(--space-4)" }}>
            <label className="switch">
              <input type="checkbox" checked={repeats}
                onChange={(event) => { setRepeats(event.target.checked); setPlan(null); }} />
              <span className="switch__track"><span className="switch__thumb" /></span>
              <span className="t-label">Repeat this meeting</span>
            </label>

            {repeats && (
              <div className="stack" style={{ gap: "var(--space-4)" }}>
                <div className="form-grid">
                  <div className="field">
                    <label className="field__label" htmlFor="freq">How often</label>
                    <select id="freq" className="select" value={frequency}
                      onChange={(event) => {
                        setFrequency(event.target.value as typeof frequency);
                        setPlan(null);
                      }}>
                      <option value="daily">Every day</option>
                      <option value="weekly">Every week</option>
                      <option value="monthly">Every month</option>
                    </select>
                  </div>
                  <div className="field">
                    <label className="field__label" htmlFor="count">Number of meetings</label>
                    <input id="count" className="input" type="number" min={2}
                      max={policy.max_recurring_occurrences} value={count}
                      onChange={(event) => { setCount(Number(event.target.value)); setPlan(null); }} />
                    <p className="field__hint">
                      Up to {policy.max_recurring_occurrences} in this room.
                    </p>
                  </div>
                </div>

                {frequency === "weekly" && (
                  <div className="stack" style={{ gap: "var(--space-2)" }}>
                    <span className="t-label">On these days</span>
                    <div className="chips">
                      {WEEKDAYS.map((day, index) => (
                        <button key={day} type="button" className="chip"
                          aria-pressed={weekdays.includes(index)}
                          onClick={() => {
                            setWeekdays((current) =>
                              current.includes(index)
                                ? current.filter((value) => value !== index)
                                : [...current, index],
                            );
                            setPlan(null);
                          }}>
                          {day}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {plan && (
                  <div className="stack" style={{ gap: "var(--space-3)" }}>
                    <Notice
                      tone={plan.blocked ? "warning" : "success"}
                      title={
                        plan.blocked
                          ? `${plan.blocked} of ${plan.occurrences.length} dates are not free`
                          : `All ${plan.bookable} dates are free`
                      }
                    >
                      {plan.blocked > 0 && (
                        <label className="checkbox" style={{ marginTop: "var(--space-2)" }}>
                          <input type="checkbox" checked={skipConflicts}
                            onChange={(event) => setSkipConflicts(event.target.checked)} />
                          <span>Book the {plan.bookable} free dates and skip the rest</span>
                        </label>
                      )}
                    </Notice>
                    <OccurrenceList plan={plan} timezone={room.timezone} />
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {policy?.explanation?.length ? (
          <details className="card">
            <summary className="card__header" style={{ cursor: "pointer" }}>
              <span className="t-label">Room rules</span>
              <Icon name="chevronDown" size={16} />
            </summary>
            <div className="card__body">
              <div className="detail-list">
                {policy.explanation.map((item) => (
                  <div className="detail-list__row" key={item.label}>
                    <span className="detail-list__key">{item.label}</span>
                    <span className="detail-list__value t-body ink-secondary">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </details>
        ) : null}
      </div>
    </Dialog>
  );
}

function OccurrenceList({ plan, timezone }: { plan: SeriesPlan; timezone: string | null }) {
  return (
    <div className="table-wrap card">
      <table className="table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Time</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {plan.occurrences.map((occurrence) => (
            <tr key={occurrence.index}>
              <td>{formatDate(occurrence.start_at, timezone)}</td>
              <td className="time">
                {formatTime(occurrence.start_at, timezone)}–
                {formatTime(occurrence.end_at, timezone)}
              </td>
              <td>
                {occurrence.ok ? (
                  <StatusPill tone={occurrence.requires_approval ? "soon" : "available"}>
                    {occurrence.requires_approval ? "Needs approval" : "Free"}
                  </StatusPill>
                ) : (
                  <span className="row" style={{ gap: "var(--space-2)" }}>
                    <StatusPill tone="occupied">Taken</StatusPill>
                    <span className="t-caption ink-secondary">{occurrence.reason}</span>
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
